import React, { useRef, useState, useEffect } from "react";
import {
  FiCopy,
  FiChevronDown,
  FiX,
  FiHeart,
  FiThumbsDown,
  FiBookmark,
} from "react-icons/fi";
import { PiCoinVertical } from "react-icons/pi";
import { PiWalletLight } from "react-icons/pi";

const cyan = "14,165,233";

const WATERMARK_ICONS = {
  wallet: PiWalletLight,
  token: PiCoinVertical,
};

const TILE_ROW_HEIGHT = 160;
const VISIBLE_ROWS = 2;
const GAP = 16;
const VIEWPORT_HEIGHT =
  TILE_ROW_HEIGHT * VISIBLE_ROWS + GAP * (VISIBLE_ROWS - 1) + 32;

// ─── Card ─────────────────────────────────────────────────────────────────────
const AssetCard = ({ asset, onOpen }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (e, text) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.log(e);
    }
  };

  const WatermarkIcon = WATERMARK_ICONS[asset.asset_type] || null;

  return (
    <div
      className="crypto-tile-card relative rounded-lg border border-mainDivider bg-transparent p-4 flex flex-col overflow-hidden hover:border-velBlue/40 dark:hover:border-velBlue/40"
      style={{
        background: "transparent",
        cursor: onOpen ? "pointer" : "default",
      }}
      onClick={() => onOpen?.(asset)}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${cyan},0.06) 0%, transparent 100%)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Row 1: coin name + title badge */}
      <div className="flex flex-row items-center gap-2 w-full">
        <span className="text-[13px] font-semibold tracking-wide text-mainText truncate shrink-0">
          {asset.coin}
        </span>
        {asset.title && (
          <span className="text-[11px] bg-mainButtonBg text-mutedText rounded-full px-2.5 py-0.5 tracking-wide truncate">
            {asset.title}
          </span>
        )}
      </div>

      {/* Row 2: exchange badge */}
      <div className="flex flex-row flex-wrap gap-1.5 mt-2">
        {asset.exchange && (
          <span className="text-[10px] bg-mainButtonBg text-mutedText rounded-full px-2.5 py-0.5 tracking-wide">
            {asset.exchange}
          </span>
        )}
      </div>

      {/* Row 3: copy address */}
      {asset.address && (
        <div className="flex items-center gap-2 mt-2.5">
          <button
            onClick={(e) => copyToClipboard(e, asset.address)}
            className="shrink-0 transition-colors duration-200 rounded cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
            style={{ color: copied ? `rgba(${cyan},0.9)` : undefined }}
          >
            {copied ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ) : (
              <FiCopy className="w-3.5 h-3.5 text-mutedText hover:text-mainText" />
            )}
          </button>
          <span
            className="text-[11px] tracking-wide text-mutedText truncate font-mono"
            title={asset.address}
          >
            {asset.address}
          </span>
        </div>
      )}

      {/* Row 4: like / dislike / catalog indicators — only when truthy */}
      {(asset.like || asset.dislike || asset.catalog) && (
        <div className="flex items-center gap-2.5 mt-2.5 pt-2.5 border-t border-mainDivider">
          {asset.like && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-400/80">
              <FiHeart className="w-3 h-3" /> Liked
            </span>
          )}
          {asset.dislike && (
            <span className="flex items-center gap-1 text-[10px] text-rose-400/80">
              <FiThumbsDown className="w-3 h-3" /> Disliked
            </span>
          )}
          {asset.catalog && (
            <span
              className="flex items-center gap-1 text-[10px]"
              style={{ color: `rgba(${cyan},0.75)` }}
            >
              <FiBookmark className="w-3 h-3" /> Catalogued
            </span>
          )}
        </div>
      )}

      {/* Watermark */}
      {WatermarkIcon && (
        <WatermarkIcon
          className="absolute right-4 pointer-events-none"
          style={{
            fontSize: "8rem",
            opacity: 0.07,
            top: "50%",
            transform: "translateY(-50%)",
            color: `rgba(${cyan},0.35)`,
          }}
        />
      )}
    </div>
  );
};

// ─── Modal ────────────────────────────────────────────────────────────────────
const AllModal = ({ data, onClose, onOpen }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .crypto-modal { animation: modal-in 180ms ease-out both; }
        .crypto-modal-scroll { scrollbar-width: thin; scrollbar-color: rgba(${cyan},0.25) transparent; }
        .crypto-modal-scroll::-webkit-scrollbar { width: 4px; }
        .crypto-modal-scroll::-webkit-scrollbar-track { background: transparent; }
        .crypto-modal-scroll::-webkit-scrollbar-thumb { background: rgba(${cyan},0.25); border-radius: 2px; }
      `}</style>

      <div className="crypto-modal relative w-full max-w-4xl max-h-[80vh] flex flex-col rounded-xl border border-surfaceBorder bg-surface/80 backdrop-blur-md shadow-login-panel overflow-hidden">
        <div className="flex items-center justify-end px-5 py-4 shrink-0 border-b border-surfaceBorder">
          <button
            onClick={onClose}
            className="text-mutedText hover:text-mainText transition-colors duration-150 rounded-md p-1 cursor-pointer"
            aria-label="Close"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="crypto-modal-scroll overflow-y-auto flex-1 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {data.map((item, i) => {
              try {
                return <AssetCard key={i} asset={item} onOpen={onOpen} />;
              } catch {
                return null;
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const CryptoTile = (props) => {
  const scrollRef = useRef(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const data = props?.companyCryptoData ?? [];
  const total = data.length;
  const onOpen = props?.onOpen;

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 8);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [data]);

  if (!total) return null;

  return (
    <>
      <style>{`
        @keyframes crypto-tile-pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(${cyan},0.06), 0 0 12px 2px rgba(${cyan},0.08); }
          50%       { box-shadow: 0 0 0 1px rgba(${cyan},0.12), 0 0 16px 3px rgba(${cyan},0.13); }
        }
        @keyframes crypto-scroll-bounce {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50%       { transform: translateY(3px); opacity: 1; }
        }
        .crypto-tile-card { border: 1px solid transparent; transition: all 200ms ease-out; }
        .crypto-tile-card:hover { animation: crypto-tile-pulse 5s ease-in-out infinite; }
        .crypto-scroll-viewport { scrollbar-width: thin; scrollbar-color: rgba(${cyan},0.25) transparent; }
        .crypto-scroll-viewport::-webkit-scrollbar { width: 4px; }
        .crypto-scroll-viewport::-webkit-scrollbar-track { background: transparent; }
        .crypto-scroll-viewport::-webkit-scrollbar-thumb { background: rgba(${cyan},0.25); border-radius: 2px; }
        .crypto-scroll-hint { animation: crypto-scroll-bounce 1.8s ease-in-out infinite; }
        .more-chip { transition: background 150ms ease, border-color 150ms ease; }
        .more-chip:hover { background: rgba(${cyan},0.15) !important; border-color: rgba(${cyan},0.35) !important; }
      `}</style>

      <div className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md shadow-login-panel overflow-hidden">
        <div className="flex flex-col gap-2 w-full text-mainText">
          {/* Header row */}
          {total > 4 && (
            <div className="flex items-center justify-end px-4 pt-3">
              <button
                onClick={() => setModalOpen(true)}
                className="more-chip cursor-pointer rounded-full px-3 py-0.5 text-[11px] font-medium tracking-wide border"
                style={{
                  background: `rgba(${cyan},0.07)`,
                  borderColor: `rgba(${cyan},0.18)`,
                  color: `rgba(${cyan},0.8)`,
                }}
              >
                +{total - 4} more
              </button>
            </div>
          )}

          {/* Viewport */}
          <div className="relative w-full" style={{ height: VIEWPORT_HEIGHT }}>
            <div
              className="pointer-events-none absolute top-0 left-0 right-0 z-10 h-10 transition-opacity duration-300"
              style={{
                opacity: canScrollUp ? 1 : 0,
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)",
              }}
            />

            <div
              ref={scrollRef}
              className="crypto-scroll-viewport h-full overflow-y-auto"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                {data.map((item, i) => {
                  try {
                    return <AssetCard key={i} asset={item} onOpen={onOpen} />;
                  } catch {
                    return null;
                  }
                })}
              </div>
            </div>

            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-14 flex flex-col items-center justify-end pb-2 transition-opacity duration-300"
              style={{
                opacity: canScrollDown ? 1 : 0,
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)",
              }}
            >
              <div className="crypto-scroll-hint">
                <FiChevronDown
                  className="w-3.5 h-3.5"
                  style={{ color: `rgba(${cyan},0.6)` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <AllModal
          data={data}
          onClose={() => setModalOpen(false)}
          onOpen={onOpen}
        />
      )}
    </>
  );
};

export default CryptoTile;
